import React, { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';

import {
  SecurityAssetSecurityType,
  type V1CreateRepeatTradeRequest,
  type SecurityAsset,
} from '@fintronners/react-api/src';
import useExternalAccounts, {
  AccountType,
} from '@fintronners/react-widgets/src/hooks/useExternalAccounts';
import { convertISODateStringToObject } from '@fintronners/react-api/src/utils/dateUtils';
import {
  MUTUAL_FUND_DECIMAL_PRECISION,
  NumberNoNaN,
  decimalRoundUp,
  trimToThreeDecimalPlaces,
} from '@fintronners/react-utils/src/numberUtilsTSX';
import fLogger from '@fintronners/react-utils/src/fLogger';

import {
  V1CreateTradeRequest,
  V1MarketSide,
  V1TradeExpirationType,
} from '@fintronners/react-api/src/tsoai';
import useUserAccount from '@/hooks/api/useUserAccount';
import useCachedMarketData from '@fintronners/react-widgets/src/hooks/market/useCachedMarketData';

import useTradeApiService from '@/hooks/api/useTradeService';
import { useSidePanel } from '@/context/SidePanelContext';
import { useSecurityAssetDetails } from '@/hooks/api/useSecurityAssetDetails';
import { useSecurityTradingData } from '@/hooks/api/useSecurityTradingData';
import { OrderType, QuantityType } from '@fintronners/internal-core/src/trade/constants';

import { TradeSidePanelForm } from './TradeSidePanelForm';
import { TradeSidePanelConfirmation } from './TradeSidePanelConfirmation';
import { TradeSidePanelSuccess } from './TradeSidePanelSuccess';
import { ErrorSidePanel } from '../ErrorSidePanel/ErrorSidePanel';
import { TradeFormData, TradeSide, tradeSchema } from './schema';
import useCurrentAccountType from '@/hooks/utils/useCurrentAccountType';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';
import { TradeFormSteps } from './constants';
import { useTradeBreakdown } from '@/hooks/trade/useTradeBreakdown';

const TradeSidePanel = () => {
  const router = useRouter();
  const [side, setSide] = useState<TradeSide>(TradeSide.BUY);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const accountType = useCurrentAccountType();
  const { userAccount } = useUserAccount(accountType);
  const { fiatAsset: fiat } = useCachedMarketData();
  const {
    currentFlowStep,
    sidePanelProps,
    goTo,
    goBack,
    setShowBackLink,
    setShowCloseButton,
    closeSidePanel,
  } = useSidePanel();

  const goToErrorPanel = () => goTo(TradeFormSteps.ERROR);
  const goToSummaryPanel = () => goTo(TradeFormSteps.SUMMARY);
  const goToSuccessPanel = () => goTo(TradeFormSteps.SUCCESS);

  const { reset: resetTradeFee } = usePreviewTradeFeeStore();

  useEffect(() => {
    if (sidePanelProps?.side) {
      setSide(sidePanelProps.side);
    }
  }, [sidePanelProps]);

  useEffect(() => {
    return () => {
      resetTradeFee();
    };
  }, [resetTradeFee]);

  const tradeApiService = useTradeApiService();

  const securityId = router.query['security-id'] as string;
  const { data: securityDetails, loading: loadingSecurityDetails } = useSecurityAssetDetails({
    securityId,
  });

  const asset = securityDetails?.securityAssets.edges?.[0]?.node as SecurityAsset;
  const fiatId = fiat?.id ?? '';
  const isMutualFund = asset?.securityType === SecurityAssetSecurityType.MutualFund;
  const isFractionable = asset?.isFractionable ?? true;

  const { data: tradingData } = useSecurityTradingData({
    fiatId,
    securityId,
    portfolioId: userAccount?.firstPortfolioId ?? '',
  });

  const { firstExternalAccount, fetchingAccounts } = useExternalAccounts(AccountType.BROKERAGE);

  const fiatBalances = tradingData?.userAccountPortfolioAssets?.edges?.find(
    (edge) => edge?.node?.asset?.id === fiatId,
  )?.node?.balances;

  const securityBalances = tradingData?.userAccountPortfolioAssets?.edges?.find(
    (edge) => edge?.node?.asset?.id === securityId,
  )?.node?.balances;

  const formContext = useMemo(() => ({ side, isFractionable }), [side, isFractionable]);
  const form = useForm<TradeFormData>({
    resolver: yupResolver(tradeSchema),
    context: formContext,
  });

  useEffect(() => {
    const defaultFormValues = tradeSchema.getDefault({ context: formContext });

    form.reset(defaultFormValues);
  }, [asset, formContext]);

  // Rounding up the current asset price to 2 decimal places before calculations
  // to make sure all the results match what the user sees on other parts of the UI
  const currentAssetPrice = decimalRoundUp(asset?.market?.last, 2);
  const dollarBalance = NumberNoNaN(fiatBalances?.tradeable);
  const shareBalance = NumberNoNaN(securityBalances?.tradeable);

  const tradeBreakdown = useTradeBreakdown({
    side,
    formData: form.watch(),
    shareBalance,
    dollarBalance,
    currentAssetPrice,
  });
  const { dollarAmount, shareAmount, dollarInputInSharesAtLimitPrice, isSellingAll } =
    tradeBreakdown;

  const performTrade = async (tradeFormData: TradeFormData) => {
    const { orderType, quantityType, duration, limitPrice, stopLoss } = tradeFormData;
    const isLimitBuy = orderType === OrderType.LIMIT_BUY;
    const isStopLoss = orderType === OrderType.STOP_LOSS;
    const isRecurring = orderType === OrderType.REPEAT_INVESTMENT;
    const isDollarsMode = quantityType === QuantityType.DOLLARS;

    if (isRecurring) {
      onPerformRecurringTrade(tradeFormData);
      return;
    }

    let base;
    let quote;
    let all;

    switch (side) {
      case TradeSide.BUY:
        if (isDollarsMode) {
          quote = dollarAmount.toString();
        } else {
          base = shareAmount.toString();
        }
        break;
      case TradeSide.SELL:
        // When it's in dollar mode, we can't just send `quote` like for BUY.
        // For some reason BE still require us to send `base` for SELL.
        if (isSellingAll) {
          all = {};
        } else if (isStopLoss && isDollarsMode) {
          base = dollarInputInSharesAtLimitPrice.toString();
        } else {
          base = shareAmount.toString();
        }

        if (isMutualFund && base) {
          const baseRoundUp = decimalRoundUp(Number(base), MUTUAL_FUND_DECIMAL_PRECISION);

          base = trimToThreeDecimalPlaces(baseRoundUp.toString());
        }

        break;
    }

    const tradeRequest: V1CreateTradeRequest = {
      portfolioUid: userAccount?.firstPortfolioId,
      tradeOrder: {
        side: side === TradeSide.BUY ? V1MarketSide.Buy : V1MarketSide.Sell,
        instrument: {
          base: asset.id,
          quote: fiatId,
        },
        base: base,
        quote: quote,
        all: all,
        expirationType: isLimitBuy || isStopLoss ? duration : V1TradeExpirationType.Day,
        mit: isLimitBuy ? { limit: limitPrice?.toString() } : undefined,
        stopLoss: isStopLoss ? { stop: stopLoss } : undefined,
      },
    };

    try {
      setIsSubmitting(true);
      await tradeApiService.tradeServiceCreateTrade(tradeRequest);

      goToSuccessPanel();
    } catch (err) {
      fLogger.error(`Error creating ${orderType} trade`);
      goToErrorPanel();
    } finally {
      setShowBackLink(false);
      setShowCloseButton(true);
      setIsSubmitting(false);
    }
  };

  const onPerformRecurringTrade = async ({
    amount,
    interval,
    selectedStartDate,
  }: TradeFormData) => {
    const tradeRequest: V1CreateRepeatTradeRequest = {
      portfolioUid: userAccount?.firstPortfolioId,
      amount: amount.toString(),
      assetUid: asset.id,
      fundingAccountUid: firstExternalAccount?.id,
      interval: interval,
      start: convertISODateStringToObject(selectedStartDate),
    };

    try {
      setIsSubmitting(true);
      await tradeApiService.tradeServiceCreateRepeatTrade(tradeRequest);

      goToSuccessPanel();
    } catch (err) {
      fLogger.error('Error creating repeat investment trade');
      goToErrorPanel();
    } finally {
      setShowBackLink(false);
      setShowCloseButton(true);
      setIsSubmitting(false);
    }
  };

  return (
    <FormProvider {...form}>
      <form>
        {currentFlowStep === TradeFormSteps.FORM && (
          <TradeSidePanelForm
            side={side}
            asset={asset}
            tradeBreakdown={tradeBreakdown}
            account={firstExternalAccount}
            loading={loadingSecurityDetails || fetchingAccounts}
            onContinue={goToSummaryPanel}
            isMutualFund={isMutualFund}
          />
        )}
        {currentFlowStep === TradeFormSteps.SUMMARY && (
          <TradeSidePanelConfirmation
            side={side}
            asset={asset}
            tradeBreakdown={tradeBreakdown}
            isSubmitting={isSubmitting}
            onBack={goBack}
            onContinue={form.handleSubmit(performTrade)}
          />
        )}
        {currentFlowStep === TradeFormSteps.SUCCESS && (
          <TradeSidePanelSuccess onClose={closeSidePanel} />
        )}
        {currentFlowStep === TradeFormSteps.ERROR && (
          <ErrorSidePanel onBack={() => goTo(TradeFormSteps.FORM)} />
        )}
      </form>
    </FormProvider>
  );
};

export default TradeSidePanel;
